
import { useCallback, useEffect, useRef } from "react";
import { useModalStore } from "@/services/modalStore";
import { visualLog } from "@/components/debug/VisualDebugger";

/**
 * Hook semplificato per gestire le modali con il tasto back
 * 
 * @param {Object} options - Opzioni di configurazione
 * @param {boolean} options.isOpen - Se la modale è aperta
 * @param {string} options.modalName - Nome della modale per la registrazione
 * @param {function} options.onOpenChange - Callback per gestire i cambiamenti di stato
 */


export function useModalBackButton({
  isOpen,
  modalName,
  onOpenChange,
}: {
  isOpen: boolean;
  modalName: string;
  onOpenChange: (open: boolean) => void;
}) {
  const openModal = useModalStore((s) => s.openModal);
  const closeModal = useModalStore((s) => s.closeModal);
  const isModalOpen = useModalStore((s) => s.isModalOpen);
  const activeModal = useModalStore((s) => s.activeModal);
  const modalHistory = useModalStore((s) => s.modalHistory);

  const mountedRef = useRef(false);


  // Registra la modale dopo il primo render solo quando isOpen diventa true
  useEffect(() => {
    if (mountedRef.current && isOpen && !isModalOpen(modalName)) {
      openModal(modalName);
      visualLog.modal(`Modal ${modalName} registered`);
    }
    mountedRef.current = true;
  }, [isOpen, modalName, isModalOpen, openModal]);

  // Cleanup della modale al dismount
  useEffect(() => {
    return () => {
      if (isModalOpen(modalName)) {
        visualLog.modal(`Modal ${modalName} cleanup - removing from store`);
        closeModal();
      }
    };
  }, [modalName, isModalOpen, closeModal]);

  // Chiude la modale solo se non risulta più presente nello store
  useEffect(() => {
    if (!isOpen) return;

    queueMicrotask(() => {
      const { activeModal: currentActive, modalHistory: history } =
        useModalStore.getState();

      if (currentActive !== modalName && !history.includes(modalName)) {
        onOpenChange(false);
      }
    });
  }, [activeModal, modalHistory, isOpen, modalName, onOpenChange]);




  // Gestisce i cambiamenti di stato apertura/chiusura
  const handleOpenChange = useCallback((open: boolean) => {
    if (open === isOpen) return;

    visualLog.modal(`${modalName} handleOpenChange: ${open}`);

    if (open) {
      openModal(modalName);
    } else {
      closeModal();
    }

    onOpenChange(open);
  }, [isOpen, modalName, openModal, closeModal, onOpenChange]);


  return {
    handleOpenChange
  };
}
